import { PersistedWorkflow } from '@cotera/sdk/core';

/**
 * The viewer's own IANA zone, for seeding a debounce node's timezone.
 *
 * The SDK defaults to UTC because a server-side default must not depend on
 * where the process runs, but someone configuring "once a week" in the editor
 * means *their* week. Seeding the browser's zone makes the boundary match the
 * intent, and it is stored explicitly so it never moves afterwards.
 *
 * Falls back to the SDK default when the browser reports a zone the SDK cannot
 * validate (older engines, or a value the zone table does not carry).
 */
export function resolveViewerTimezone(): string {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return PersistedWorkflow.isValidDebounceTimezone(zone)
      ? zone
      : PersistedWorkflow.DEFAULT_DEBOUNCE_TIMEZONE;
  } catch {
    return PersistedWorkflow.DEFAULT_DEBOUNCE_TIMEZONE;
  }
}

/**
 * Every IANA zone the runtime knows, for the timezone picker.
 *
 * Read from the engine rather than kept as a list here: a hand-maintained set
 * is the thing that leaves someone unable to pick the zone they actually need,
 * and the SDK validates against this same table. Older engines without
 * `supportedValuesOf` fall back to the viewer's zone plus UTC, which still
 * covers the default and the common case.
 */
export function listSelectableTimezones(): readonly string[] {
  const viewerZone = resolveViewerTimezone();
  const fallback = Array.from(
    new Set([viewerZone, PersistedWorkflow.DEFAULT_DEBOUNCE_TIMEZONE])
  );
  try {
    const supported = Intl.supportedValuesOf('timeZone');
    return supported.length > 0 ? supported : fallback;
  } catch {
    return fallback;
  }
}

/**
 * The current UTC offset of `timezone`, as `UTC+05:30`, for labelling a zone in
 * the picker. Two zones with the same city name are told apart by their offset,
 * and the offset is what someone is really choosing between.
 */
export function describeTimezoneOffset(timezone: string): string | null {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'longOffset',
    }).formatToParts(new Date());
    return parts.find((part) => part.type === 'timeZoneName')?.value ?? null;
  } catch {
    return null;
  }
}

/**
 * Formats an instant in the node's own zone. The preview has to read in the
 * zone the boundary falls in — showing the viewer's local time would hide
 * exactly the difference the timezone field exists to control.
 */
export function formatInTimezone(date: Date, timezone: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      timeZone: timezone,
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: 'numeric',
      minute: '2-digit',
    }).format(date);
  } catch {
    return date.toISOString();
  }
}

/**
 * How far off `target` is, in the largest unit that still reads naturally
 * ("in 6 hours", "in 4 days").
 *
 * The preview's most useful line: an absolute date alone does not show that a
 * calendar window is shorter than its name suggests. Seeing "in 2 days" on a
 * Saturday, under a weekly window, is what makes calendar-not-rolling obvious
 * without having to explain it.
 *
 * Returns null for an instant already past, which the caller renders as nothing
 * rather than "0 days ago".
 */
export function formatRelativeToNow(target: Date, from: Date): string | null {
  const ms = target.getTime() - from.getTime();
  if (ms <= 0) {
    return null;
  }
  try {
    const format = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
    const minutes = Math.round(ms / 60_000);
    if (minutes < 60) {
      return format.format(Math.max(minutes, 1), 'minute');
    }
    const hours = Math.round(minutes / 60);
    if (hours < 24) {
      return format.format(hours, 'hour');
    }
    const days = Math.round(hours / 24);
    if (days < 31) {
      return format.format(days, 'day');
    }
    return format.format(Math.round(days / 30), 'month');
  } catch {
    return null;
  }
}
